import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Scan,
  Thermometer,
  Calculator,
  BarChart3,
  Wrench,
  CheckCircle2,
  ExternalLink,
  ShieldAlert,
  Cpu,
  Layers,
  Sparkles,
  LayoutDashboard,
} from 'lucide-react';

interface WorkflowStep {
  step: number;
  title: string;
  description: string;
  tip?: string;
  image?: string;
  imageCaption?: string;
  telemetryTags?: string[];
}

interface WorkflowSection {
  id: string;
  title: string;
  subtitle: string;
  icon: any;
  accent: string;
  badge: string;
  steps: WorkflowStep[];
}

const workflows: WorkflowSection[] = [
  {
    id: 'cladding',
    title: 'Cladding Defect Vision Analysis',
    subtitle: 'High-resolution optical inspection with automated damage bounding boxes and severity scoring',
    icon: Scan,
    accent: 'border-emerald-500 text-emerald-600 bg-emerald-50',
    badge: 'YOLOv8x-Seg / SAM-2',
    steps: [
      {
        step: 1,
        title: 'Capture Optical Field Imagery',
        description: 'Collect high-resolution RGB imagery (minimum 1920x1080) of insulated piping, vessels, or columns using handheld cameras, robotic crawlers, or autonomous aerial drones.',
        tip: 'Ensure consistent oblique illumination without heavy cast shadows for optimal edge separation of jacket seams.',
      },
      {
        step: 2,
        title: 'Upload to InsuScan Vision Engine',
        description: 'Ingest raw imagery into the Cladding Analysis module. The system parses EXIF GPS tags and associates images with target Asset IDs in the integrity database.',
        tip: 'Batch processing supports multi-angle views of complex piping rack configurations.',
      },
      {
        step: 3,
        title: 'AI Damage Segmentation & Classification',
        description: 'The fine-tuned model identifies 6 primary CUI precursors: seam openings, caulking degradation, punctures/tears, banding failure, lap inversion, and weeping rust stains.',
        image: '/cladding_defect.jpeg',
        imageCaption: 'Macro optical field validation: Identified tear & weather jacketing seam opening (Confidence: 94.2%)',
        telemetryTags: ['DEFECT_CLASS: TEAR / PUNCTURE', 'CONFIDENCE: 94.2%', 'EST_AREA: 142 cm²', 'CORROSION_RISK: HIGH'],
        tip: 'Any defect with confidence below 75% automatically triggers secondary inspector verification.',
      },
      {
        step: 4,
        title: 'Engineering Review & Severity Validation',
        description: 'Validate annotated bounding boxes and review quantitative severity metrics before pushing findings into the facility asset health registry.',
        tip: 'Assign custom tags (e.g. Critical CUI Window, Rain Leader Runoff Zone) to prioritize downstream planning.',
      },
    ],
  },
  {
    id: 'thermal',
    title: 'Radiometric Thermal Moisture Detection',
    subtitle: 'Passive thermographic screening during transient thermal states to pinpoint trapped moisture',
    icon: Thermometer,
    accent: 'border-orange-500 text-orange-600 bg-orange-50',
    badge: 'Radiometric IR / U-Net CNN',
    steps: [
      {
        step: 1,
        title: 'Schedule Post-Sunset Screening Window',
        description: 'Conduct thermal imaging during post-sunset diurnal cooling (2 to 3 hours after dusk). Wet insulation exhibits significantly higher thermal inertia (capacitance) than dry insulation, creating unmistakable ΔT signatures.',
        tip: 'Low-emissivity polished aluminum jacketing may generate false reflections; use polarizing lens caps or scan at 45° to 60° angles.',
      },
      {
        step: 2,
        title: 'Calibrate Atmospheric & Radiometric Offsets',
        description: 'Input ambient air temperature, process fluid temperature, relative humidity, and jacketing emissivity (typically ε = 0.90 to 0.95 for weathered aluminum or oxidized stainless steel).',
        tip: 'Accurate emissivity setting prevents false-positive moisture classifications.',
      },
      {
        step: 3,
        title: 'Radiometric Matrix Ingestion & Spot Analysis',
        description: 'Upload raw radiometric FLIR RJPEG or thermal array matrices. The system executes pixel-level ΔT thresholding, isolating cold/hot moisture pools.',
        image: '/thermal_flir.jpeg',
        imageCaption: 'Radiometric thermogram: Localized heat capacitance pool indicating 86% probability of trapped moisture',
        telemetryTags: ['HOTSPOT: 37.4°C', 'BASELINE: 26.1°C', 'ΔT: +11.3°C', 'MOISTURE_PROB: 86.4%'],
        tip: 'Thermal anomalies indicate liquid presence, not confirmed wall thinning. Target high-probability zones with PEC.',
      },
      {
        step: 4,
        title: 'NDT Screening Cross-Referencing',
        description: 'Export moisture anomaly coordinates directly to the NDT inspection crew to conduct targeted non-intrusive Pulsed Eddy Current (PEC) measurements.',
        tip: 'Targeted screening eliminates 40% to 65% of unnecessary scaffolding and blanket insulation stripping.',
      },
    ],
  },
  {
    id: 'ndt',
    title: 'Pulsed Eddy Current (PEC) Screening',
    subtitle: 'Volumetric non-intrusive wall thickness verification without stripping thermal insulation',
    icon: Layers,
    accent: 'border-cyan-500 text-cyan-600 bg-cyan-50',
    badge: 'Electromagnetic NDT / Eddyfi Lyft',
    steps: [
      {
        step: 1,
        title: 'Deploy PEC Grid on Suspect Circuits',
        description: 'Position Pulsed Eddy Current electromagnetic probes over the weather jacket at locations prioritized by visual cladding defects and thermal moisture hotspots.',
        image: '/ndt_screening.jpeg',
        imageCaption: 'Field NDT technician executing Pulsed Eddy Current (PEC) electromagnetic screening through weather jacketing',
        telemetryTags: ['LIFTOFF: 50.8 mm (Insulation + Cladding)', 'AVERAGE_WALL: 9.5 mm', 'MIN_WALL: 6.8 mm', 'GRID_SPACING: 50 mm'],
        tip: 'No plant shutdown, pipe stripping, or scaffolding is required for initial screening.',
      },
      {
        step: 2,
        title: 'Map Remaining Wall Thickness Matrix',
        description: 'Generate real-time color-coded thickness maps. Identify localized corrosion under insulation (CUI) pits, general thinned areas, and boundary transition zones.',
        tip: 'Cross-correlate min measured wall thickness (t_mm) with historical baseline ultrasonic measurements.',
      },
      {
        step: 3,
        title: 'Feed Data into API 579 FFS Engine',
        description: 'Directly pass minimum wall thickness (t_mm) and longitudinal defect length (s) into the deterministic API 579-1 Part 5 Level 1 calculator.',
      },
    ],
  },
  {
    id: 'ffs',
    title: 'API 579-1 / ASME FFS-1 Fitness-For-Service',
    subtitle: 'Deterministic Level 1 engineering assessment for local metal loss under internal pressure',
    icon: Calculator,
    accent: 'border-blue-500 text-blue-600 bg-blue-50',
    badge: 'API 579-1 Part 5 Level 1',
    steps: [
      {
        step: 1,
        title: 'Collect Mechanical & Geometric Parameters',
        description: 'Retrieve component design pressure (P), allowable stress (S), weld joint efficiency (E), outer diameter (D_o), and nominal wall thickness (t_nom) from equipment data sheets.',
        tip: 'Future Corrosion Allowance (FCA) must account for projected thinning until the next scheduled turnaround.',
      },
      {
        step: 2,
        title: 'Compute Folias Bulging Factor & RSF',
        description: 'Evaluate parameter λ = 1.285 · s / √(D_o · t_min). Calculate Folias factor M_t and Remaining Strength Factor RSF = R_t / (1 - (1 - R_t) / M_t).',
        image: '/piping_isometric.jpeg',
        imageCaption: '3D Piping CAD Digital Twin: Stress concentration and localized metal loss evaluation under internal pressure',
        telemetryTags: ['RSF_ALLOWABLE: 0.900', 'COMPUTED_RSF: 0.942', 'STATUS: PASS MAWP', 'DERATED_MAWP: N/A'],
        tip: 'If RSF ≥ RSF_allow (0.90), component is safe for continued operation at design MAWP.',
      },
      {
        step: 3,
        title: 'Derate MAWP or Mandate Engineered Repair',
        description: 'For components where RSF < 0.90, the platform automatically calculates the reduced safe operating pressure MAWP_r = MAWP · (RSF / 0.90).',
        tip: 'Export the complete engineering compliance dossier with PE / API 570 digital signature block.',
      },
    ],
  },
  {
    id: 'ips',
    title: 'Inspection Priority Scoring (IPS)',
    subtitle: 'API 581 & DNV-RP-G109 multi-barrier risk ranking for plant-wide piping circuits',
    icon: BarChart3,
    accent: 'border-purple-500 text-purple-600 bg-purple-50',
    badge: 'API 581 / DNV-RP-G109',
    steps: [
      {
        step: 1,
        title: 'Parameterize Thermal CUI Susceptibility',
        description: 'Quantify operational duration in the peak CUI temperature window (-4°C to 175°C). Weight assets undergoing frequent cyclic operation or intermittent batch steaming.',
        tip: 'Thermal cycling significantly accelerates corrosion by driving moisture pumping mechanisms.',
      },
      {
        step: 2,
        title: 'Assess Geometric & Environmental Traps',
        description: 'Evaluate physical vulnerabilities: dead legs, low-point water pockets, unsealed pipe shoes, uninsulated penetrations, coastal distance, and cooling tower spray plume paths.',
      },
      {
        step: 3,
        title: 'Composite Multi-Barrier Risk Calculation',
        description: 'IPS combines 5 weighted vectors: Thermal (30%), Geometric (25%), Environmental (20%), Historical (15%), and Confidence (10%) into an actionable 0-10 priority index.',
        tip: 'IPS ≥ 7.0 automatically triggers immediate NDT field deployment within 14 calendar days.',
      },
    ],
  },
  {
    id: 'cmms',
    title: 'Closed-Loop CMMS Work Order Dispatch',
    subtitle: 'One-click dispatch of structured defect evidence dossiers to SAP Plant Maintenance or IBM Maximo',
    icon: Wrench,
    accent: 'border-amber-500 text-amber-600 bg-amber-50',
    badge: 'SAP BAPI / IBM Maximo REST',
    steps: [
      {
        step: 1,
        title: 'Compile Evidence Package',
        description: 'The platform aggregates calibrated optical photos, FLIR thermograms, PEC thickness matrices, and signed FFS calculation summaries into a standardized dossier.',
        tip: 'Eliminates repetitive field re-scoping and back-and-forth between planners and reliability teams.',
      },
      {
        step: 2,
        title: 'Select Destination CMMS & Maintenance Activity Type',
        description: 'Choose SAP PM (Notification Type M2 / Work Order PM01) or IBM Maximo (Work Order Type EM / CM) with automated priority code assignment.',
      },
      {
        step: 3,
        title: 'Execute Dispatch & Retain Bi-directional Sync',
        description: 'Receive immediate tracking references (SAP Notification ID / Maximo WO number) with continuous status synchronization back to the InsuScan dashboard.',
      },
    ],
  },
];

export function DocumentationPage() {
  const [activeWorkflow, setActiveWorkflow] = useState('cladding');

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 pb-20">
      {/* Top Navbar */}
      <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 hover:text-slate-900 hover:border-slate-400 active:scale-[0.98] transition-all shadow-xs cursor-pointer group"
              >
                <ArrowLeft className="h-3.5 w-3.5 text-slate-500 group-hover:text-slate-900 group-hover:-translate-x-0.5 transition-all" />
                <span>Back to Home</span>
              </Link>
              <div className="h-5 w-px bg-slate-300 hidden sm:block" />
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 font-semibold">
                  API 579 / 581 / 583
                </span>
                <span className="text-sm font-semibold text-slate-900 hidden md:inline">
                  Technical Architecture & Workflow Guide
                </span>
              </div>
            </div>
            <Link
              to="/app"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:scale-[0.98] transition-all shadow-sm hover:shadow-md hover:shadow-blue-500/25 border border-blue-500 cursor-pointer"
            >
              <Cpu className="h-3.5 w-3.5" />
              <span>Launch Platform</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Header */}
      <header className="bg-gradient-to-b from-white to-slate-100/80 border-b border-slate-200 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-semibold uppercase tracking-wider mb-4">
            <Cpu className="h-3.5 w-3.5" />
            Engineering Operating Guide
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            InsuScan AI Inspection Pipeline
          </h1>
          <p className="mt-3 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
            From raw drone optical & FLIR thermography capture to deterministic API 579-1 Level 1 FFS calculation and closed-loop SAP/Maximo dispatch.
          </p>

          {/* Workflow Tabs */}
          <div className="mt-8 flex flex-wrap justify-center gap-2">
            {workflows.map((wf) => {
              const Icon = wf.icon;
              const isActive = activeWorkflow === wf.id;
              return (
                <button
                  key={wf.id}
                  onClick={() => {
                    setActiveWorkflow(wf.id);
                    const el = document.getElementById(wf.id);
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className={`px-3.5 py-2 rounded-lg text-xs font-medium transition-all flex items-center gap-2 cursor-pointer border ${
                    isActive
                      ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span>{wf.title.split(' ')[0]}</span>
                </button>
              );
            })}
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 space-y-16">
        {workflows.map((wf) => {
          const Icon = wf.icon;
          return (
            <section
              key={wf.id}
              id={wf.id}
              className="scroll-mt-24 tech-panel p-6 sm:p-8 bg-white border border-slate-200 rounded-2xl shadow-sm"
            >
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-200 mb-8">
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-xl border ${wf.accent}`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-slate-900">{wf.title}</h2>
                    <p className="text-xs sm:text-sm text-slate-500 mt-0.5">{wf.subtitle}</p>
                  </div>
                </div>
                <span className="self-start sm:self-center font-mono text-xs px-2.5 py-1 rounded bg-slate-100 text-slate-700 border border-slate-300 font-semibold whitespace-nowrap">
                  {wf.badge}
                </span>
              </div>

              {/* Steps */}
              <div className="space-y-8">
                {wf.steps.map((step, idx) => (
                  <div key={idx} className="relative pl-10 sm:pl-12">
                    {/* Number Indicator */}
                    <div className="absolute left-0 top-0 h-7 w-7 sm:h-8 sm:step-8 rounded-lg bg-blue-600 text-white flex items-center justify-center font-mono font-bold text-xs sm:text-sm shadow-sm">
                      0{step.step}
                    </div>

                    {/* Step Content */}
                    <div className="space-y-3">
                      <h3 className="text-base sm:text-lg font-bold text-slate-900">
                        {step.title}
                      </h3>
                      <p className="text-sm text-slate-600 leading-relaxed">
                        {step.description}
                      </p>

                      {/* Optional Tip */}
                      {step.tip && (
                        <div className="flex items-start gap-2.5 p-3 rounded-lg bg-blue-50/80 border border-blue-200 text-xs text-blue-900">
                          <CheckCircle2 className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <strong className="font-semibold text-blue-950">Field Note:</strong> {step.tip}
                          </div>
                        </div>
                      )}

                      {/* Embedded Inspection Artifact / Image */}
                      {step.image && (
                        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-900 overflow-hidden shadow-sm">
                          <div className="relative group">
                            <img
                              src={step.image}
                              alt={step.title}
                              className="w-full max-h-96 object-cover object-center"
                            />
                            {step.telemetryTags && (
                              <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-1.5 pointer-events-none">
                                {step.telemetryTags.map((tag, tIdx) => (
                                  <span
                                    key={tIdx}
                                    className="font-mono text-[10px] px-2 py-0.5 rounded bg-slate-950/85 text-emerald-400 border border-emerald-500/40 backdrop-blur-sm shadow-sm"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          {step.imageCaption && (
                            <div className="p-3 bg-slate-950 text-slate-300 text-xs flex items-center justify-between border-t border-slate-800 font-mono">
                              <span className="flex items-center gap-2">
                                <Sparkles className="h-3.5 w-3.5 text-blue-400" />
                                {step.imageCaption}
                              </span>
                              <span className="text-[10px] text-slate-500 hidden sm:inline">INSU-SCAN TELEMETRY</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          );
        })}

        {/* Regulatory Compliance Footer Banner */}
        <section className="p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-slate-900 to-blue-950 text-white shadow-lg border border-slate-800">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-2">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded bg-blue-500/20 text-blue-300 text-xs font-mono font-medium border border-blue-500/30">
                <ShieldAlert className="h-3.5 w-3.5" />
                API RP 583 / ASME FFS-1 / OSHA 1910.119
              </div>
              <h3 className="text-xl font-bold">Standard Operating Procedure Compliance</h3>
              <p className="text-slate-300 text-xs sm:text-sm max-w-2xl leading-relaxed">
                All algorithmic screenings and fitness-for-service dispositions meet the strict procedural guidelines of API 570 (Piping Inspection Code), API 510 (Pressure Vessels), and DNV-RP-G109 barrier management.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to="/app/ffs-calculator"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold text-white bg-blue-600 hover:bg-blue-500 active:scale-[0.98] transition-all shadow-md shadow-blue-950/40 border border-blue-400/40 cursor-pointer"
              >
                <Calculator className="h-4 w-4" />
                <span>Open FFS Calculator</span>
                <ExternalLink className="h-3.5 w-3.5 opacity-80" />
              </Link>
              <Link
                to="/app"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold text-slate-200 hover:text-white bg-slate-800/90 hover:bg-slate-700 active:scale-[0.98] transition-all border border-slate-700/80 hover:border-slate-500 shadow-sm cursor-pointer"
              >
                <LayoutDashboard className="h-4 w-4 text-blue-400" />
                <span>Go to Dashboard</span>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
